import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Edit01, Plus, RefreshCw01, Star01, Trash01 } from "@untitledui/icons";
import { Table } from "@/components/application/table/table";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { AppDialog } from "@/components/app/AppDialog";
import { ApiError } from "@/lib/apiClient";
import { AppSelect } from "@/components/app/AppSelect";
import { IconAction } from "@/components/app/IconAction";
import {
  useBulkUpdatePaatVersions,
  useCreatePaatProfile,
  useDeletePaatProfile,
  usePaatPlatforms,
  usePaatProfilesList,
  useSetDefaultPaatProfile,
  useUpdatePaatProfile,
  type PaatProfile,
  type PaatProfileBody,
} from "@/lib/paatProfiles";

const inputCls =
  "w-full rounded-lg bg-primary px-3 py-2 text-sm text-primary shadow-xs ring-1 ring-primary outline-none focus:ring-2 focus:ring-brand";

function apiErrText(e: unknown): string {
  if (e instanceof ApiError) {
    const body = e.body as { message?: string; error?: string } | string | null;
    if (typeof body === "string" && body) return body;
    if (body && typeof body === "object") return body.message ?? body.error ?? e.message;
  }
  return e instanceof Error ? e.message : String(e);
}

export function PaatProfilesPage() {
  const { t } = useTranslation();
  const { data, isLoading, isError } = usePaatProfilesList();
  const setDefault = useSetDefaultPaatProfile();
  const [creating, setCreating] = useState(false);
  const [editProfile, setEditProfile] = useState<PaatProfile | null>(null);
  const [deleteProfile, setDeleteProfile] = useState<PaatProfile | null>(null);
  const [bulk, setBulk] = useState(false);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-display-sm font-semibold text-primary">{t("paat.title")}</h1>
          <p className="mt-1 text-md text-tertiary">{t("paat.subtitle")}</p>
        </div>
        <div className="flex gap-2">
          <Button size="md" color="secondary" iconLeading={RefreshCw01} onPress={() => setBulk(true)}>
            {t("paat.bulkUpdate")}
          </Button>
          <Button size="md" color="primary" iconLeading={Plus} onPress={() => setCreating(true)}>
            {t("paat.add")}
          </Button>
        </div>
      </div>

      {isLoading && <LoadingIndicator />}
      {isError && <p className="text-sm text-error-primary">{t("common.loadError")}</p>}

      {data && (
        <div className="overflow-hidden rounded-xl ring-1 ring-secondary">
          <Table aria-label={t("paat.title")} selectionMode="none">
            <Table.Header>
              <Table.Head isRowHeader id="id" label={t("paat.col.id")} />
              <Table.Head id="profileName" label={t("paat.col.name")} />
              <Table.Head id="platform" label={t("paat.col.platform")} />
              <Table.Head id="device" label={t("paat.col.device")} />
              <Table.Head id="appVersion" label={t("paat.col.appVersion")} />
              <Table.Head id="sdkVersion" label={t("paat.col.sdkVersion")} />
              <Table.Head id="versionCode" label={t("paat.col.versionCode")} />
              <Table.Head id="apiLevel" label={t("paat.col.apiLevel")} />
              <Table.Head id="usageCount" label={t("paat.col.usage")} />
              <Table.Head id="isDefault" label={t("paat.col.default")} />
              <Table.Head id="actions" label="" />
            </Table.Header>
            <Table.Body items={data}>
              {(p) => (
                <Table.Row id={p.id}>
                  <Table.Cell className="font-mono font-medium text-primary">{p.id}</Table.Cell>
                  <Table.Cell>{p.profileName}</Table.Cell>
                  <Table.Cell>
                    <Badge type="pill-color" size="sm" color="gray">
                      {p.platform}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell className="text-xs text-tertiary">
                    {[p.deviceManufacturer, p.deviceModel].filter(Boolean).join(" ") || "—"}
                  </Table.Cell>
                  <Table.Cell>{p.appVersion ?? "—"}</Table.Cell>
                  <Table.Cell>{p.sdkVersion ?? "—"}</Table.Cell>
                  <Table.Cell>{p.versionCode ?? "—"}</Table.Cell>
                  <Table.Cell>{p.apiLevel ?? "—"}</Table.Cell>
                  <Table.Cell>{p.usageCount ?? 0}</Table.Cell>
                  <Table.Cell>
                    {p.isDefault && (
                      <Badge type="pill-color" size="sm" color="success">
                        {t("paat.default")}
                      </Badge>
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    <div className="flex justify-end gap-1">
                      {!p.isDefault && (
                        <IconAction
                          icon={Star01}
                          label={t("paat.setDefault")}
                          isDisabled={setDefault.isPending}
                          onPress={() => setDefault.mutate(p.id)}
                        />
                      )}
                      <IconAction icon={Edit01} label={t("common.edit")} onPress={() => setEditProfile(p)} />
                      {(p.usageCount ?? 0) === 0 && (
                        <IconAction icon={Trash01} label={t("common.delete")} onPress={() => setDeleteProfile(p)} />
                      )}
                    </div>
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table>
        </div>
      )}

      {creating && <ProfileFormModal onClose={() => setCreating(false)} />}
      {editProfile && <ProfileFormModal profile={editProfile} onClose={() => setEditProfile(null)} />}
      {deleteProfile && <DeleteModal profile={deleteProfile} onClose={() => setDeleteProfile(null)} />}
      {bulk && <BulkUpdateModal onClose={() => setBulk(false)} />}
    </div>
  );
}

const ID_PATTERN = /^[a-zA-Z0-9_-]+$/;

const EMPTY: PaatProfileBody & { id: string } = {
  id: "",
  profileName: "",
  versionCode: "4160200",
  appVersion: "4.16.2",
  sdkVersion: "5.0.0",
  apiLevel: "",
  language: "EN",
  platform: "",
  deviceModel: "",
  deviceName: "",
  deviceManufacturer: "",
};

function ProfileFormModal({ profile, onClose }: { profile?: PaatProfile; onClose: () => void }) {
  const { t } = useTranslation();
  const editing = !!profile;
  const platforms = usePaatPlatforms();
  const create = useCreatePaatProfile();
  const update = useUpdatePaatProfile(profile?.id ?? "");

  const [f, setF] = useState<PaatProfileBody & { id: string }>(() =>
    profile
      ? {
          id: profile.id,
          profileName: profile.profileName,
          versionCode: profile.versionCode ?? "",
          appVersion: profile.appVersion ?? "",
          sdkVersion: profile.sdkVersion ?? "",
          apiLevel: profile.apiLevel ?? "",
          language: profile.language ?? "",
          platform: profile.platform,
          deviceModel: profile.deviceModel ?? "",
          deviceName: profile.deviceName ?? "",
          deviceManufacturer: profile.deviceManufacturer ?? "",
        }
      : EMPTY,
  );
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((s) => ({ ...s, [k]: v }));
  const [err, setErr] = useState<string | null>(null);

  const required: (keyof typeof f)[] = editing
    ? ["profileName", "appVersion", "sdkVersion", "apiLevel", "language", "platform", "deviceModel", "deviceName", "deviceManufacturer"]
    : ["id", "profileName", "appVersion", "sdkVersion", "apiLevel", "language", "platform", "deviceModel", "deviceName", "deviceManufacturer"];

  const submit = async () => {
    setErr(null);
    for (const k of required) {
      if (!String(f[k] ?? "").trim()) {
        setErr(t("paat.fillRequired"));
        return;
      }
    }
    if (!editing && !ID_PATTERN.test(f.id.trim())) {
      setErr(t("paat.idPattern"));
      return;
    }
    const body: PaatProfileBody = {
      profileName: f.profileName,
      versionCode: f.versionCode || null,
      appVersion: f.appVersion,
      sdkVersion: f.sdkVersion,
      apiLevel: f.apiLevel,
      language: f.language,
      platform: f.platform,
      deviceModel: f.deviceModel,
      deviceName: f.deviceName,
      deviceManufacturer: f.deviceManufacturer,
    };
    try {
      if (editing) await update.mutateAsync(body);
      else await create.mutateAsync({ ...body, id: f.id });
      onClose();
    } catch (e) {
      setErr(apiErrText(e));
    }
  };

  const pending = create.isPending || update.isPending;

  return (
    <AppDialog
      isOpen
      size="xl"
      onClose={onClose}
      title={editing ? t("paat.editTitle") : t("paat.add")}
      footer={
        <>
          <Button color="secondary" onPress={onClose}>
            {t("common.cancel")}
          </Button>
          <Button color="primary" isDisabled={pending} onPress={() => void submit()}>
            {t("common.save")}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
        {err && (
          <div className="col-span-full rounded-lg bg-error-primary px-3 py-2 text-sm text-error-primary ring-1 ring-error">{err}</div>
        )}
        {!editing && (
          <Field label={`${t("paat.col.id")} *`} hint={t("paat.idHint")}>
            <input
              className={inputCls}
              autoFocus
              pattern="^[a-zA-Z0-9_-]+$"
              value={f.id}
              onChange={(e) => set("id", e.target.value)}
              placeholder="samsungS24"
            />
          </Field>
        )}
        <Field label={`${t("paat.col.name")} *`}>
          <input className={inputCls} value={f.profileName} onChange={(e) => set("profileName", e.target.value)} />
        </Field>
        <Field label={`${t("paat.col.platform")} *`}>
          <AppSelect value={f.platform} onChange={(e) => set("platform", e.target.value)}>
            <option value="">{t("paat.selectPlatform")}</option>
            {(platforms.data ?? []).map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </AppSelect>
        </Field>
        <Field label={`${t("paat.col.appVersion")} *`}>
          <input className={inputCls} value={f.appVersion} onChange={(e) => set("appVersion", e.target.value)} />
        </Field>
        <Field label={`${t("paat.col.sdkVersion")} *`}>
          <input className={inputCls} value={f.sdkVersion} onChange={(e) => set("sdkVersion", e.target.value)} />
        </Field>
        <Field label={t("paat.versionCode")}>
          <input className={inputCls} value={f.versionCode ?? ""} onChange={(e) => set("versionCode", e.target.value)} />
        </Field>
        <Field label={`${t("paat.apiLevel")} *`}>
          <input className={inputCls} value={f.apiLevel} onChange={(e) => set("apiLevel", e.target.value)} />
        </Field>
        <Field label={`${t("paat.language")} *`}>
          <input className={inputCls} value={f.language} onChange={(e) => set("language", e.target.value)} placeholder="cs" />
        </Field>
        <Field label={`${t("paat.deviceModel")} *`}>
          <input className={inputCls} value={f.deviceModel} onChange={(e) => set("deviceModel", e.target.value)} />
        </Field>
        <Field label={`${t("paat.deviceName")} *`}>
          <input className={inputCls} value={f.deviceName} onChange={(e) => set("deviceName", e.target.value)} />
        </Field>
        <Field label={`${t("paat.deviceManufacturer")} *`}>
          <input className={inputCls} value={f.deviceManufacturer} onChange={(e) => set("deviceManufacturer", e.target.value)} />
        </Field>
      </div>
    </AppDialog>
  );
}

function DeleteModal({ profile, onClose }: { profile: PaatProfile; onClose: () => void }) {
  const { t } = useTranslation();
  const del = useDeletePaatProfile();
  const [err, setErr] = useState<string | null>(null);
  const submit = async () => {
    setErr(null);
    try {
      await del.mutateAsync(profile.id);
      onClose();
    } catch (e) {
      setErr(apiErrText(e));
    }
  };
  return (
    <AppDialog
      isOpen
      onClose={onClose}
      title={t("paat.deleteTitle")}
      description={t("paat.deleteConfirm", { id: profile.id })}
      footer={
        <>
          <Button color="secondary" onPress={onClose}>
            {t("common.cancel")}
          </Button>
          <Button color="primary-destructive" isDisabled={del.isPending} onPress={() => void submit()}>
            {t("common.delete")}
          </Button>
        </>
      }
    >
      {err && <div className="rounded-lg bg-error-primary px-3 py-2 text-sm text-error-primary ring-1 ring-error">{err}</div>}
    </AppDialog>
  );
}

function BulkUpdateModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const platforms = usePaatPlatforms();
  const bulk = useBulkUpdatePaatVersions();
  const [platform, setPlatform] = useState("");
  const [versionCode, setVersionCode] = useState("");
  const [appVersion, setAppVersion] = useState("");
  const [sdkVersion, setSdkVersion] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  const changes: string[] = [];
  if (versionCode.trim()) changes.push(`${t("paat.versionCode")}: ${versionCode.trim()}`);
  if (appVersion.trim()) changes.push(`${t("paat.col.appVersion")}: ${appVersion.trim()}`);
  if (sdkVersion.trim()) changes.push(`${t("paat.col.sdkVersion")}: ${sdkVersion.trim()}`);

  // Reset confirmation whenever the inputs change so the summary can't go stale.
  const editField = (setter: (v: string) => void) => (v: string) => {
    setter(v);
    setConfirming(false);
    setOk(null);
  };

  const review = () => {
    setErr(null);
    setOk(null);
    if (!platform) return setErr(t("paat.selectPlatform"));
    if (!changes.length) return setErr(t("paat.bulkNeedVersion"));
    setConfirming(true);
  };

  const apply = async () => {
    setErr(null);
    try {
      const res = await bulk.mutateAsync({
        platform,
        versionCode: versionCode.trim() || null,
        appVersion: appVersion.trim() || null,
        sdkVersion: sdkVersion.trim() || null,
      });
      setOk(res.message);
      setConfirming(false);
    } catch (e) {
      setErr(apiErrText(e));
    }
  };

  return (
    <AppDialog
      isOpen
      onClose={onClose}
      title={t("paat.bulkUpdate")}
      description={t("paat.bulkHint")}
      footer={
        <>
          <Button color="secondary" onPress={onClose}>
            {t("common.done")}
          </Button>
          {confirming ? (
            <Button color="primary" isDisabled={bulk.isPending} onPress={() => void apply()}>
              {t("paat.bulkConfirm")}
            </Button>
          ) : (
            <Button color="primary" isDisabled={bulk.isPending} onPress={review}>
              {t("paat.bulkApply")}
            </Button>
          )}
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {err && <div className="rounded-lg bg-error-primary px-3 py-2 text-sm text-error-primary ring-1 ring-error">{err}</div>}
        {ok && <div className="rounded-lg bg-secondary px-3 py-2 text-sm text-success-primary ring-1 ring-secondary">{ok}</div>}
        {confirming && !ok && (
          <div className="rounded-lg bg-secondary px-3 py-2 text-sm text-secondary ring-1 ring-secondary">
            <p className="font-medium text-primary">{t("paat.bulkConfirmSummary", { platform })}</p>
            <ul className="mt-1 list-disc pl-5">
              {changes.map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
          </div>
        )}
        <Field label={`${t("paat.col.platform")} *`}>
          <AppSelect value={platform} onChange={(e) => editField(setPlatform)(e.target.value)}>
            <option value="">{t("paat.selectPlatform")}</option>
            {(platforms.data ?? []).map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </AppSelect>
        </Field>
        <Field label={t("paat.col.appVersion")}>
          <input className={inputCls} value={appVersion} onChange={(e) => editField(setAppVersion)(e.target.value)} />
        </Field>
        <Field label={t("paat.col.sdkVersion")}>
          <input className={inputCls} value={sdkVersion} onChange={(e) => editField(setSdkVersion)(e.target.value)} />
        </Field>
        <Field label={t("paat.versionCode")}>
          <input className={inputCls} value={versionCode} onChange={(e) => editField(setVersionCode)(e.target.value)} />
        </Field>
      </div>
    </AppDialog>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-sm font-medium text-secondary">{label}</span>
      {children}
      {hint && <span className="text-xs text-tertiary">{hint}</span>}
    </label>
  );
}
